import { Controller } from "@hotwired/stimulus";

// Manages the form permissions page (/forms/:id/permissions). The page is
// rendered fully server-side; this controller handles the "Add permission"
// modal submit and per-row deletes via the existing API endpoints. After
// any mutation we reload the page to keep server-rendered counts in sync —
// no DOM diffing needed.
export default class extends Controller {
  static targets = [
    "addForm",
    "modeCard",
    "userField", "userSelect",
    "roleField", "poleSelect", "posteSelect",
    "canView", "canViewResponses", "canEdit",
    "error"
  ];

  static values = {
    createUrl:               String,
    updateUrlTemplate:       String,
    csrfToken:               String
  };

  connect() {
    this._mode = "user";
  }

  switchMode(event) {
    const mode = event.currentTarget.value;
    this._mode = mode;
    this.modeCardTargets.forEach((card) => {
      card.classList.toggle("csm_mode-card--active", card.dataset.mode === mode);
    });
    if (this.hasUserFieldTarget) this.userFieldTarget.hidden = mode !== "user";
    if (this.hasRoleFieldTarget) this.roleFieldTarget.hidden = mode !== "role";
    this._hideError();
  }

  async submitPermission(event) {
    event?.preventDefault?.();
    this._hideError();

    const mode = this._mode || "user";
    const can_view          = !!this.canViewTarget?.checked;
    const can_view_responses = !!this.canViewResponsesTarget?.checked;
    const can_edit          = !!this.canEditTarget?.checked;

    if (!can_view && !can_view_responses && !can_edit) {
      this._showError("Active au moins une permission (Voir, Réponses, ou Modifier).");
      return;
    }

    const payload = { permission_type: mode, can_view, can_view_responses, can_edit };

    if (mode === "user") {
      const userId = this.userSelectTarget?.value;
      if (!userId) { this._showError("Choisis un membre."); return; }
      payload.user_id = userId;
    } else {
      const poleId  = this.poleSelectTarget?.value;
      const posteId = this.posteSelectTarget?.value;
      if (!poleId || !posteId) { this._showError("Choisis un pôle ET un poste."); return; }
      payload.pole_id = poleId;
      payload.poste_id = posteId;
    }

    try {
      const res = await fetch(this.createUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ permission: payload })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err.errors || ["Création impossible"]).join(", "));
      }
      // Reload so the freshly added permission shows up in the list with the
      // correct beneficiary label (avoids a custom DOM-build path here).
      window.location.reload();
    } catch (e) {
      this._showError(e.message);
    }
  }

  async deletePermission(event) {
    const id = event.currentTarget.dataset.permissionId;
    if (!id) return;
    if (!confirm("Supprimer cette permission ? L'utilisateur perdra immédiatement son accès.")) return;

    const url = this.updateUrlTemplateValue.replace("__id__", id);
    try {
      const res = await fetch(url, {
        method: "DELETE",
        headers: this._jsonHeaders(),
        credentials: "same-origin"
      });
      if (!res.ok && res.status !== 204) {
        throw new Error("Suppression impossible.");
      }
      const row = event.currentTarget.closest("tr[data-permission-id]");
      row?.remove();
      // If no rows left, reload to show the empty-state card.
      const remaining = this.element.querySelectorAll('tr[data-permission-id]').length;
      if (remaining === 0) window.location.reload();
    } catch (e) {
      alert(e.message);
    }
  }

  // ── helpers ─────────────────────────────────────────────────────────

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": this.csrfTokenValue || (document.querySelector('meta[name="csrf-token"]')?.content || "")
    };
  }

  _showError(msg) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = msg || "Une erreur est survenue.";
    this.errorTarget.hidden = false;
  }

  _hideError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }
}
